import striptags from 'striptags';

import config from '../configs';

export const StripTags = {
  methods: {
    striptags(content) {
      return striptags(content, config.striptags.simple);
    },
  },
};

export const RenderStatusTag = {
  methods: {
    renderStatusTag(home) {
      return home.underOffer ? 'Under Offer' : home.statusTag || null;
    },
  },
};

export const RenderPriceString = {
  methods: {
    renderPriceString(home) {
      return home.price !== null
        ? `${
            home.__typename === 'homesForSale_new_Entry' ? 'From ' : ''
          }${new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(home.price)}`
        : null;
    },
  },
};

export const RenderDiscountedPriceString = {
  methods: {
    renderDiscountedPriceString(home) {
      return home.price !== null
        ? `${new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(home.price)}`
        : null;
    },
  },
};

export const RenderWasPriceString = {
  methods: {
    renderWasPriceString(home) {
      return home.wasPrice !== null
        ? `${new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(home.wasPrice)}`
        : null;
    },
  },
};

export const RenderSaleRibbonText = {
  methods: {
    renderSaleRibbonText(home) {
      return home.saleRibbonText !== null ? `${home.saleRibbonText}` : null;
    },
  },
};

export const GetHomeHeadline = {
  methods: {
    getHomeHeadline(home) {
      return home.content !== null
        ? home.content
        : striptags(this.defaultHomeHeadline, config.striptags.simple);
    },
  },
};

export const StartingPrice = {
  methods: {
    startingPrice(homes) {
      const homePrices = homes
        .filter(home => home.price !== null)
        .map(home => home.price);

      return homePrices > 1000000
        ? `$${Math.floor(Math.min(...homePrices) / 10000) / 100}m`
        : `$${Math.floor(Math.min(...homePrices) / 1000)}k`;
    },
  },
};

export const CombineHomeImages = {
  methods: {
    combineHomeImages(home) {
      return [...home.heroImage, ...home.galleryImages];
    },
  },
};

export const ReadMore = {
  data() {
    return {
      readMoreReady: false,
      readMoreVisible: false,
    };
  },
  mounted() {
    this.readMoreReady = true;
  },
  methods: {
    showReadMore() {
      this.readMoreVisible = true;
    },
    hideReadMore() {
      this.readMoreVisible = false;
    },
  },
};

export const GetCommunityColourTextClass = {
  methods: {
    getCommunityColourTextClass(colourObj) {
      if (colourObj !== null) {
        const colourClass = JSON.parse(colourObj.color[0])?.class;
        return colourClass ? `text-community-${colourClass}` : 'text-primary';
      } else {
        return 'text-primary';
      }
    },
  },
};

export const RenderCommunityAddress = {
  methods: {
    renderCommunityAddress(community) {
      if (
        community.communityStreetAddress &&
        community.communityCity &&
        community.communityState &&
        community.communityPostcode
      ) {
        return `${community.communityStreetAddress}, ${community.communityCity} ${community.communityState} ${community.communityPostcode}`;
      } else {
        return '';
      }
    },
  },
};

export const GetLocationDataByPostcode = {
  methods: {
    getLocationDataByPostcode(postcode) {
      return new Promise(async (resolve, reject) => {
        try {
          const { Geocoder } = await google.maps.importLibrary('geocoding');
          const geocoder = new Geocoder();
          geocoder.geocode(
            {
              componentRestrictions: {
                country: 'AU',
                postalCode: postcode,
              },
            },
            function(results, status) {
              if (status == 'OK') {
                const result = results[0];
                const state = result.address_components.find(component =>
                  component.types.includes('administrative_area_level_1')
                )?.long_name;

                if (state === 'Victoria') {
                  resolve({
                    suburb: result.address_components.find(
                      component =>
                        component.types.includes('locality') &&
                        component.types.includes('political')
                    )?.long_name,
                    postcode: result.address_components.find(component =>
                      component.types.includes('postal_code')
                    )?.long_name,
                    latitude: results[0].geometry.location.lat(),
                    longitude: results[0].geometry.location.lng(),
                  });
                } else {
                  reject('NOT_VIC');
                }
              } else {
                reject(status);
              }
            }
          );
        } catch (error) {
          reject(error);
        }
      });
    },
  },
};

export const GetLocationDataBySuburb = {
  methods: {
    getLocationDataBySuburb(suburb) {
      return new Promise(async (resolve, reject) => {
        try {
          const { Geocoder } = await google.maps.importLibrary('geocoding');
          const geocoder = new Geocoder();
          geocoder.geocode(
            {
              address: suburb,
              componentRestrictions: {
                country: 'AU',
                administrativeArea: 'locality',
              },
            },
            function(results, status) {
              if (status == 'OK') {
                results = results.filter(result => {
                  return result.formatted_address !== 'Australia';
                });

                if (results.length) {
                  const firstVictorianResult = results.find(result => {
                    const IS_IN_VICTORIA =
                      result.address_components.find(component =>
                        component.types.includes('administrative_area_level_1')
                      )?.long_name === 'Victoria';
                    const HAS_A_POSTCODE = result.address_components.find(
                      component =>
                        component.types.includes('postal_code') !== undefined
                    );
                    return IS_IN_VICTORIA && HAS_A_POSTCODE;
                  });

                  if (firstVictorianResult !== undefined) {
                    resolve({
                      suburb: firstVictorianResult.address_components.find(
                        component =>
                          component.types.includes('locality') &&
                          component.types.includes('political')
                      )?.long_name,
                      postcode: firstVictorianResult.address_components.find(
                        component => component.types.includes('postal_code')
                      )?.long_name,
                      latitude: firstVictorianResult.geometry.location.lat(),
                      longitude: firstVictorianResult.geometry.location.lng(),
                    });
                  } else {
                    reject('NOT_VIC');
                  }
                } else {
                  reject('ZERO_RESULTS');
                }
              } else {
                reject(status);
              }
            }
          );
        } catch (error) {
          reject(error);
        }
      });
    },
  },
};

export const GetNearestCommunities = {
  methods: {
    getNearestCommunities({ latitude, longitude, resultCount }, communities) {
      return new Promise(async (resolve, reject) => {
        try {
          const { spherical } = await google.maps.importLibrary('geometry');

          const communityPromises = communities.map(community => {
            return new Promise(async (resolve, reject) => {
              try {
                const distanceInMetres = spherical.computeDistanceBetween(
                  new google.maps.LatLng(latitude, longitude),
                  new google.maps.LatLng(
                    community.latitude,
                    community.longitude
                  )
                );

                resolve({
                  id: community.id,
                  distanceTo: parseFloat((distanceInMetres / 1000).toFixed(2)),
                });
              } catch (error) {
                reject(error);
              }
            });
          });

          try {
            const results = await Promise.all(communityPromises);
            const sortedResults = results.sort((a, b) => {
              return a.distanceTo - b.distanceTo;
            });
            const topResults = sortedResults.slice(0, resultCount);
            resolve(topResults);
          } catch (error) {
            reject(error);
          }
        } catch (error) {
          reject(error);
        }
      });
    },
  },
};
